import type { IResource } from "@gettactic/api";

/** The part of a slot this module needs — shared by `ISlot` and the agenda's `ReducedSlot`. */
export interface ScheduleRoomSlot {
  resource: IResource | null;
}

export interface ScheduleWithRoomSlots {
  slots?: ScheduleRoomSlot[] | null;
}

/**
 * The distinct meeting rooms a reservation holds, in the order its slots come back.
 *
 * A reservation stores one slot per room *per attendee* (the API requires every user to
 * hold every resource), so the same room appears once per attendee and the slot count
 * says nothing about how many rooms were booked — only the distinct resources do.
 */
export function getScheduleRooms(
  schedule: ScheduleWithRoomSlots | null | undefined
): IResource[] {
  const rooms: IResource[] = [];
  const seenRoomIds = new Set<string>();

  for (const slot of schedule?.slots ?? []) {
    const resource = slot?.resource;
    if (resource?.type !== "meeting_room" || seenRoomIds.has(resource.id)) {
      continue;
    }
    seenRoomIds.add(resource.id);
    rooms.push(resource);
  }

  return rooms;
}

/** True when one reservation books several meeting rooms at once. */
export function isMultiRoomSchedule(
  schedule: ScheduleWithRoomSlots | null | undefined
): boolean {
  return getScheduleRooms(schedule).length > 1;
}
